import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, X, Minimize2, Maximize2, Loader2, CheckCircle, Zap, Sparkles } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { EchobotChatProps, ChatMessage, EchobotTaskToCreate } from '../../types/echobot';
import { getAILanguage, type SupportedLanguage } from '../../utils/aiLanguageHelper';
import { tasksService } from '../../services/tasksService';
import { auth } from '../../config/firebase';
import { unifiedAgentService, SUGGESTED_QUESTIONS } from '../../services/unifiedAgentService';
import { geminiService } from '../../services/geminiService';
import { assistantFallbackService } from '../../services/assistantFallbackService';

function isMutationIntent(query: string): boolean {
  return /\b(create|add|make|new|set up|remind|erinnere|erstelle|erstellen|anlegen|hinzufügen|neu)\b/i.test(query);
}

const EchobotChat: React.FC<EchobotChatProps> = ({
  onClose,
  currentContext,
  onActionClick
}) => {
  const { t, i18n } = useTranslation(['ai', 'common']);
  
  // Get current language for AI responses
  const currentLanguage = getAILanguage().code;
  
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      role: 'echobot',
      content: currentLanguage === 'de' 
        ? 'Hallo! 👋 Wie kann ich dir helfen?' 
        : 'Hello! 👋 How can I help you?',
      timestamp: new Date()
    }
  ]);
  // Draft input is persisted per chat context so an unsent message survives a chat/context switch
  const draftKey = `echobot-draft-${currentContext || 'default'}`;
  const [input, setInput] = useState<string>(() => {
    try { return localStorage.getItem(`echobot-draft-${currentContext || 'default'}`) || ''; } catch { return ''; }
  });
  const updateInput = (val: string) => {
    setInput(val);
    try { if (val) localStorage.setItem(draftKey, val); else localStorage.removeItem(draftKey); } catch { /* ignore */ }
  };
  const [isTyping, setIsTyping] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [useMCPMode, setUseMCPMode] = useState(false);
  const [mcpInitialized, setMcpInitialized] = useState(false);
  const [lastToolCall, setLastToolCall] = useState<string | null>(null);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Initialize unified agent service for MCP mode
  useEffect(() => {
    const initMCP = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) return;
        
        const userProfileStr = localStorage.getItem('userProfile');
        const userProfile = userProfileStr ? JSON.parse(userProfileStr) : null;
        const orgId = userProfile?.orgId || currentUser.uid;
        
        await unifiedAgentService.initialize({
          orgId,
          userId: currentUser.uid,
          language: currentLanguage as 'de' | 'en'
        });
        
        setMcpInitialized(true);
        console.log('✅ Echobot: MCP Mode initialized');
      } catch (error) {
        console.error('Failed to initialize MCP mode:', error);
      }
    };
    
    initMCP();
  }, [currentLanguage]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input on mount
  useEffect(() => {
    if (!isMinimized) {
      inputRef.current?.focus();
    }
  }, [isMinimized]);

  // Restore the per-context draft when the chat context changes
  useEffect(() => {
    try { setInput(localStorage.getItem(draftKey) || ''); } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentContext]);

  // Load chat history from localStorage on mount
  useEffect(() => {
    const savedHistory = localStorage.getItem('echobot-chat-history');
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory);
        // Restore dates
        const restored = parsed.map((msg: any) => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
        setMessages(restored);
      } catch (e) {
        console.error('Failed to load chat history:', e);
      }
    }
  }, []);

  // Save chat history to localStorage
  useEffect(() => {
    // Only save last 20 messages
    const toSave = messages.slice(-20);
    localStorage.setItem('echobot-chat-history', JSON.stringify(toSave));
  }, [messages]);

  // Suggested questions based on context and language
  const getSuggestedQuestions = () => {
    const suggestionsDE: Record<string, string[]> = {
      dashboard: [
        "Erstelle einen Task für morgen",
        "Wie erstelle ich einen Lead?",
        "Was bedeuten die Dashboard-Zahlen?"
      ],
      'google-ads': [
        "Wie erstelle ich eine Kampagne?",
        "Warum ist meine CTR niedrig?",
        "Wie finde ich gute Keywords?"
      ],
      opportunities: [
        "Was ist MEDDPICC?",
        "Wie qualifiziere ich einen Deal?",
        "Wann soll ich nachfassen?"
      ],
      tasks: [
        "Erstelle einen Task für morgen",
        "Wie organisiere ich meine Tasks?",
        "Was sind Task-Gruppen?"
      ],
      leads: [
        "Wie importiere ich Leads?",
        "Was ist Lead-Scoring?",
        "Wie weise ich Leads zu?"
      ],
      onboarding: [
        "Was muss ich als nächstes tun?",
        "Wie funktioniert das CRM?",
        "Welche Module brauche ich?"
      ]
    };
    
    const suggestionsEN: Record<string, string[]> = {
      dashboard: [
        "Create a task for tomorrow",
        "How do I create a lead?",
        "What do the dashboard numbers mean?"
      ],
      'google-ads': [
        "How do I create a campaign?",
        "Why is my CTR low?",
        "How do I find good keywords?"
      ],
      opportunities: [
        "What is MEDDPICC?",
        "How do I qualify a deal?",
        "When should I follow up?"
      ],
      tasks: [
        "Create a task for tomorrow",
        "How do I organize my tasks?",
        "What are task groups?"
      ],
      leads: [
        "How do I import leads?",
        "What is lead scoring?",
        "How do I assign leads?"
      ],
      onboarding: [
        "What do I need to do next?",
        "How does the CRM work?",
        "Which modules do I need?"
      ]
    };
    
    const suggestions = currentLanguage === 'de' ? suggestionsDE : suggestionsEN;
    const defaultSuggestions = currentLanguage === 'de' 
      ? ["Wie kann ich dir helfen?", "Was sind die wichtigsten Features?", "Zeig mir eine Tour!"]
      : ["How can I help you?", "What are the main features?", "Show me a tour!"];

    return suggestions[currentContext.page] || defaultSuggestions;
  };

  // Helper function to create a task from Echobot response
  const createTaskFromEchobot = async (taskData: EchobotTaskToCreate): Promise<{ id: string; title: string } | null> => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        console.error('Echobot: No authenticated user for task creation');
        return null;
      }

      // Get user profile from localStorage or auth
      const userProfileStr = localStorage.getItem('userProfile');
      const userProfile = userProfileStr ? JSON.parse(userProfileStr) : null;
      const orgId = userProfile?.orgId || currentUser.uid;
      const userName = userProfile?.name || currentUser.displayName || currentUser.email || 'User';

      // Parse the due date
      const dueDate = new Date(taskData.dueDate);
      if (isNaN(dueDate.getTime())) {
        // Fallback to tomorrow if date parsing fails
        dueDate.setDate(new Date().getDate() + 1);
      }

      // Create the task
      const taskId = await tasksService.createTask({
        title: taskData.title,
        description: taskData.description || '',
        dueDate: dueDate,
        priority: taskData.priority,
        type: taskData.type,
        completed: false,
        assignedTo: currentUser.uid,
        visibility: 'private',
        orgId: orgId,
        createdBy: currentUser.uid,
        createdByName: userName
      });

      console.log('✅ Echobot created task:', taskId, taskData.title);
      return { id: taskId, title: taskData.title };
    } catch (error) {
      console.error('❌ Echobot failed to create task:', error);
      return null;
    }
  };

  const buildFallbackResponse = (query: string): ChatMessage => {
    const fallback = assistantFallbackService.answer(query, {
      tasks: currentContext.userData?.tasks as any,
      contacts: currentContext.userData?.leads as any,
      opportunities: currentContext.userData?.opportunities as any,
      companies: []
    });

    const navigationActionByFallbackAction: Record<string, string> = {
      navigate_to_contacts: 'navigate-to-leads',
      navigate_to_tasks: 'navigate-to-tasks',
      navigate_to_opportunities: 'navigate-to-opportunities',
      navigate_to_companies: 'navigate-to-companies',
      navigate_to_time_tracking: 'navigate-to-time-tracking'
    };

    const actionButtons = (fallback.cards || []).slice(0, 3).map(card => ({
      label: card.deepLink.label,
      action: card.deepLink.itemType === 'contact'
        ? 'navigate-to-leads'
        : card.deepLink.itemType === 'opportunity'
          ? 'navigate-to-opportunities'
          : card.deepLink.itemType === 'company'
            ? 'navigate-to-companies'
            : 'navigate-to-tasks'
    }));

    if (actionButtons.length === 0 && fallback.action && navigationActionByFallbackAction[fallback.action]) {
      actionButtons.push({
        label: currentLanguage === 'de' ? 'Öffnen' : 'Open',
        action: navigationActionByFallbackAction[fallback.action]
      });
    }

    return {
      id: `msg-${Date.now()}-fallback`,
      role: 'echobot',
      content: fallback.response,
      timestamp: new Date(),
      animation: fallback.cards?.length ? 'question' : 'wave',
      actionButtons
    };
  };

  const handleSend = async () => {
    if (!input.trim() || isTyping) return;

    // Check for commands
    if (input.startsWith('/')) {
      handleCommand(input);
      updateInput('');
      return;
    }

    // Add user message
    const userMsg: ChatMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMsg]);
    const userInput = input;
    updateInput('');
    setIsTyping(true);
    setLastToolCall(null);

    try {
      const localFallback = assistantFallbackService.answer(userInput, {
        tasks: currentContext.userData?.tasks as any,
        contacts: currentContext.userData?.leads as any,
        opportunities: currentContext.userData?.opportunities as any,
        companies: []
      });

      if (!isMutationIntent(userInput) && localFallback.action !== 'assistant_general') {
        setMessages(prev => [...prev, buildFallbackResponse(userInput)]);
        return;
      }

      // Use MCP mode if enabled and initialized
      if (useMCPMode && mcpInitialized) {
        console.log('🔧 Using MCP Mode for Echobot');
        
        const response = await unifiedAgentService.processInput(userInput, currentContext);
        
        // Track tool calls
        if (response.toolCalls && response.toolCalls.length > 0) {
          setLastToolCall(response.toolCalls[0].name);
        }
        
        // Build the response message
        let messageContent = response.message;
        
        // Handle task creation from MCP response
        let createdTask: { id: string; title: string } | null = null;
        if (response.taskToCreate) {
          console.log('🤖 MCP Mode: Creating task:', response.taskToCreate);
          createdTask = await createTaskFromEchobot(response.taskToCreate);
          if (createdTask) {
            const taskConfirm = currentLanguage === 'de'
              ? `\n\n✅ Task "${createdTask.title}" wurde erstellt!`
              : `\n\n✅ Task "${createdTask.title}" has been created!`;
            messageContent += taskConfirm;
          }
        }
        
        // Add tool execution info if present
        if (response.toolCalls && response.toolCalls.length > 0) {
          const toolInfo = currentLanguage === 'de'
            ? `\n\n🔧 *Ausgeführt: ${response.toolCalls.map(t => t.name).join(', ')}*`
            : `\n\n🔧 *Executed: ${response.toolCalls.map(t => t.name).join(', ')}*`;
          messageContent += toolInfo;
        }

        // Add echobot response
        const botMsg: ChatMessage = {
          id: `msg-${Date.now()}-bot`,
          role: 'echobot',
          content: messageContent,
          timestamp: new Date(),
          animation: createdTask ? 'celebrate' : (response.animation || 'wave'),
          actionButtons: response.actionButtons,
          taskCreated: createdTask || undefined
        };

        setMessages(prev => [...prev, botMsg]);
        
      } else {
        if (!geminiService.isConfigured()) {
          setMessages(prev => [...prev, buildFallbackResponse(userInput)]);
          return;
        }

        // Standard Echobot mode using geminiService
        const response = await geminiService.chatWithEchobot(
          userInput,
          messages.map(m => ({ role: m.role, content: m.content })),
          {
            ...currentContext,
            language: currentLanguage,
            userData: currentContext.userData
          }
        );

        // Handle task creation if the AI wants to create a task
        let createdTask: { id: string; title: string } | null = null;
        if (response.taskToCreate) {
          console.log('🤖 Echobot wants to create task:', response.taskToCreate);
          createdTask = await createTaskFromEchobot(response.taskToCreate);
        }

        // Build the response message
        let messageContent = response.message;
        
        // If task was created, append confirmation
        if (createdTask) {
          const taskConfirm = currentLanguage === 'de'
            ? `\n\n✅ Task "${createdTask.title}" wurde erstellt!`
            : `\n\n✅ Task "${createdTask.title}" has been created!`;
          messageContent += taskConfirm;
        } else if (response.taskToCreate && !createdTask) {
          const taskError = currentLanguage === 'de'
            ? '\n\n⚠️ Task konnte nicht erstellt werden. Bitte melde dich an.'
            : '\n\n⚠️ Could not create task. Please log in.';
          messageContent += taskError;
        }

        // Add echobot response
        const botMsg: ChatMessage = {
          id: `msg-${Date.now()}-bot`,
          role: 'echobot',
          content: messageContent,
          timestamp: new Date(),
          animation: createdTask ? 'celebrate' : response.animation,
          actionButtons: response.actionButtons,
          taskCreated: createdTask || undefined
        };

        setMessages(prev => [...prev, botMsg]);
      }
    } catch (error) {
      console.error('Echobot chat error:', error);
      setMessages(prev => [...prev, buildFallbackResponse(userInput)]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleCommand = (command: string) => {
    const cmd = command.toLowerCase().trim();
    const isDE = currentLanguage === 'de';
    
    let responseMsg: ChatMessage = {
      id: `cmd-${Date.now()}`,
      role: 'echobot',
      content: '',
      timestamp: new Date()
    };

    switch (cmd) {
      case '/help':
        responseMsg.content = isDE 
          ? `**Verfügbare Befehle:**
        
/help - Zeigt diese Hilfe
/clear - Löscht den Chat
/dashboard - Erklärt das Dashboard
/campaign - Kampagnen-Hilfe
        
Du kannst mir auch einfach Fragen stellen! 😊`
          : `**Available commands:**
        
/help - Shows this help
/clear - Clears the chat
/dashboard - Explains the dashboard
/campaign - Campaign help
        
You can also just ask me questions! 😊`;
        break;

      case '/clear':
        setMessages([{
          id: '1',
          role: 'echobot',
          content: isDE 
            ? 'Chat wurde geleert. Wie kann ich dir helfen? 🤖'
            : 'Chat cleared. How can I help you? 🤖',
          timestamp: new Date()
        }]);
        localStorage.removeItem('echobot-chat-history');
        return;

      case '/dashboard':
        responseMsg.content = isDE 
          ? 'Das Dashboard zeigt dir die wichtigsten Kennzahlen auf einen Blick: Leads, Opportunities, Tasks und mehr! Nutze die Quick Actions für schnellen Zugriff. 📊'
          : 'The dashboard shows you the most important metrics at a glance: Leads, Opportunities, Tasks and more! Use Quick Actions for fast access. 📊';
        responseMsg.actionButtons = [{
          label: isDE ? 'Zum Dashboard' : 'Go to Dashboard',
          action: 'navigate-to-dashboard'
        }];
        break;

      case '/campaign':
        responseMsg.content = isDE 
          ? 'Um eine Kampagne zu erstellen, gehe zu Google Ads und klicke auf "Neue Kampagne". Ich helfe dir Schritt für Schritt! 🚀'
          : 'To create a campaign, go to Google Ads and click "New Campaign". I\'ll help you step by step! 🚀';
        responseMsg.actionButtons = [{
          label: isDE ? 'Kampagne erstellen' : 'Create Campaign',
          action: 'navigate-to-google-ads'
        }];
        break;

      default:
        responseMsg.content = isDE 
          ? `Unbekannter Befehl: ${cmd}. Tippe /help für verfügbare Befehle.`
          : `Unknown command: ${cmd}. Type /help for available commands.`;
    }

    setMessages(prev => [...prev, responseMsg]);
  };

  const handleQuestionClick = (question: string) => {
    updateInput(question);
    inputRef.current?.focus();
  };

  const handleActionButton = (action: string) => {
    if (onActionClick) {
      onActionClick(action);
    }
  };

  const handleClearChat = () => {
    const confirmMsg = currentLanguage === 'de' ? 'Chat-Verlauf löschen?' : 'Clear chat history?';
    if (confirm(confirmMsg)) {
      setMessages([{
        id: '1',
        role: 'echobot',
        content: currentLanguage === 'de' 
          ? 'Chat wurde geleert. Stell mir eine neue Frage! 🤖'
          : 'Chat cleared. Ask me a new question! 🤖',
        timestamp: new Date()
      }]);
      localStorage.removeItem('echobot-chat-history');
    }
  };

  if (isMinimized) {
    return (
      <div className="fixed bottom-32 right-6 z-50">
        <button
          onClick={() => setIsMinimized(false)}
          className="bg-bg-card border border-default text-txt px-4 py-2 rounded-pill shadow-card hover:shadow-card transition-all flex items-center gap-2"
        >
          <Maximize2 className="w-4 h-4" />
          <span className="font-semibold">
            {currentLanguage === 'de' ? 'Chat mit Echobot' : 'Chat with Echobot'}
          </span>
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-32 right-6 z-50 w-full max-w-md md:w-[400px]">
      {/* Chat Container */}
      <div className="bg-bg-card rounded-2xl shadow-heavy flex flex-col h-[500px] max-h-[80vh] overflow-hidden border border-accent">
        {/* Header */}
        <div className="bg-bg-raised border-b border-default px-4 py-3 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-accent-soft rounded-full flex items-center justify-center">
              🤖
            </div>
            <div>
              <h3 className="font-bold text-sm text-txt">
                {currentLanguage === 'de' ? 'Chat mit Echobot' : 'Chat with Echobot'}
              </h3>
              <p className="text-xs text-txt-tertiary">
                {currentLanguage === 'de' ? 'Dein KI-Assistent' : 'Your AI Assistant'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* MCP Mode Toggle */}
            <button
              onClick={() => setUseMCPMode(!useMCPMode)}
              className={`p-1.5 rounded-lg transition-colors flex items-center gap-1 text-xs ${
                useMCPMode 
                  ? 'bg-warning-soft text-warning' 
                  : 'hover:bg-bg-card text-txt-tertiary'
              }`}
              title={currentLanguage === 'de' 
                ? (useMCPMode ? 'CRM-Aktionen aktiv' : 'CRM-Aktionen aktivieren')
                : (useMCPMode ? 'CRM actions active' : 'Enable CRM actions')
              }
            >
              <Zap className={`w-3.5 h-3.5 ${useMCPMode ? 'animate-pulse' : ''}`} />
              {useMCPMode && <span className="text-[10px] font-bold">MCP</span>}
            </button>
            <button
              onClick={() => setIsMinimized(true)}
              className="p-1.5 hover:bg-bg-card rounded-lg transition-colors"
              title={currentLanguage === 'de' ? 'Minimieren' : 'Minimize'}
            >
              <Minimize2 className="w-4 h-4" />
            </button>
            <button
              onClick={handleClearChat}
              className="p-1.5 hover:bg-bg-card rounded-lg transition-colors text-xs"
              title={currentLanguage === 'de' ? 'Chat löschen' : 'Clear chat'}
            >
              🗑️
            </button>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-bg-card rounded-lg transition-colors"
              title={currentLanguage === 'de' ? 'Schließen' : 'Close'}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* MCP Mode Banner */}
        {useMCPMode && (
          <div className="px-4 py-2 bg-warning-soft border-b border-subtle flex items-center justify-between">
            <div className="flex items-center gap-2 text-xs text-warning">
              <Zap className="w-3.5 h-3.5 text-warning" />
              <span className="font-medium">
                {currentLanguage === 'de' 
                  ? 'CRM-Aktionen aktiviert' 
                  : 'CRM Actions Enabled'
                }
              </span>
              <span className="text-warning">
                {currentLanguage === 'de' 
                  ? '- Leads, Tasks, Briefings erstellen' 
                  : '- Create leads, tasks, briefings'
                }
              </span>
            </div>
            {lastToolCall && (
              <span className="text-[10px] bg-warning-soft text-warning px-2 py-0.5 rounded-full">
                {lastToolCall}
              </span>
            )}
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-bg-surface">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                  msg.role === 'user'
                    ? 'bg-accent-soft text-txt'
                    : 'bg-bg-card border border-default text-txt shadow-card'
                }`}
              >
                <div className="text-sm leading-relaxed">
                  <ReactMarkdown
                    components={{
                      // Clean, professional chat formatting
                      p: ({ children }) => <p className="my-2 leading-relaxed">{children}</p>,
                      ul: ({ children }) => <ul className="my-2 space-y-1">{children}</ul>,
                      ol: ({ children }) => <ol className="my-2 space-y-1 list-decimal list-inside">{children}</ol>,
                      li: ({ children }) => (
                        <li className="flex items-start gap-2">
                          <span className="text-accent mt-1">•</span>
                          <span>{children}</span>
                        </li>
                      ),
                      strong: ({ children }) => <strong className="font-bold text-accent">{children}</strong>,
                      em: ({ children }) => <em className="italic text-txt-secondary">{children}</em>,
                      h1: ({ children }) => <h3 className="text-base font-bold mt-3 mb-2 text-accent border-b border-subtle pb-1">{children}</h3>,
                      h2: ({ children }) => <h4 className="text-sm font-bold mt-3 mb-1.5 text-accent">{children}</h4>,
                      h3: ({ children }) => <h5 className="text-sm font-semibold mt-2 mb-1 text-txt-secondary">{children}</h5>,
                      // Data cards for metrics
                      code: ({ children }) => (
                        <code className="bg-accent-soft text-accent px-1.5 py-0.5 rounded text-xs font-mono">{children}</code>
                      ),
                      // Better blockquote for highlights
                      blockquote: ({ children }) => (
                        <blockquote className="border-l-3 border-accent pl-3 my-2 bg-accent-soft py-2 rounded-r-lg italic text-txt-secondary">
                          {children}
                        </blockquote>
                      ),
                    }}
                  >
                    {msg.content}
                  </ReactMarkdown>
                </div>
                
                {/* Task Created Indicator */}
                {msg.taskCreated && (
                  <div className="mt-2 flex items-center gap-2 text-success bg-success-soft px-3 py-1.5 rounded-lg">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-xs font-medium">
                      {currentLanguage === 'de' ? 'Task erstellt' : 'Task created'}
                    </span>
                  </div>
                )}
                
                {/* Action Buttons */}
                {msg.actionButtons && msg.actionButtons.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {msg.actionButtons.map((btn, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleActionButton(btn.action)}
                        className="text-xs px-3 py-1.5 bg-accent-soft text-accent rounded-lg hover:bg-accent-soft transition-colors font-medium"
                      >
                        {btn.label}
                      </button>
                    ))}
                  </div>
                )}
                
                <p className="text-xs opacity-60 mt-1">
                  {msg.timestamp.toLocaleTimeString('de-DE', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </p>
              </div>
            </div>
          ))}

          {/* Typing Indicator */}
          {isTyping && (
            <div className="flex justify-start">
              <div className="bg-bg-card border border-accent rounded-2xl px-4 py-3 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-accent" />
                <span className="text-sm text-txt-secondary">
                  {currentLanguage === 'de' ? 'Echobot tippt...' : 'Echobot is typing...'}
                </span>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Suggested Questions */}
        {messages.length <= 2 && (
          <div className="px-4 py-3 bg-bg-surface border-t border-subtle">
            <p className="text-xs font-semibold text-txt-tertiary mb-2">
              💡 {currentLanguage === 'de' ? 'Vorgeschlagene Fragen:' : 'Suggested questions:'}
            </p>
            <div className="flex flex-wrap gap-2">
              {getSuggestedQuestions().slice(0, 3).map((question, idx) => (
                <button
                  key={idx}
                  onClick={() => handleQuestionClick(question)}
                  className="text-xs px-3 py-1.5 bg-bg-card border border-default text-txt-secondary rounded-pill hover:border-accent hover:text-accent transition-all"
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="p-4 bg-bg-card border-t border-default rounded-b-2xl">
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => updateInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder={currentLanguage === 'de' ? 'Frag mich etwas...' : 'Ask me something...'}
              disabled={isTyping}
              className="flex-1 px-4 py-2 border border-strong rounded-xl text-sm focus:ring-2 focus:ring-accent/30 focus:border-accent disabled:opacity-50 disabled:cursor-not-allowed"
            />
            <button
              onClick={handleSend}
              disabled={isTyping || !input.trim()}
              className="p-2 bg-accent text-txt-inverted rounded-xl hover:bg-accent-bright disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
          <p className="text-xs text-txt-tertiary mt-2">
            {currentLanguage === 'de' 
              ? <>Tipp: Tippe <code className="bg-bg-surface px-1 rounded">/help</code> für Befehle</>
              : <>Tip: Type <code className="bg-bg-surface px-1 rounded">/help</code> for commands</>
            }
          </p>
        </div>
      </div>

      {/* Mobile Styles */}
      <style>{`
        @media (max-width: 768px) {
          .fixed.bottom-24.right-6 {
            bottom: 0;
            right: 0;
            left: 0;
            max-width: 100%;
          }
          
          .fixed.bottom-24.right-6 > div {
            border-radius: 0;
            height: 100vh;
            max-height: 100vh;
          }
        }
      `}</style>
    </div>
  );
};

export default EchobotChat;
